import { formatPhoneNumberIntl } from 'react-phone-number-input';
import crypto from 'crypto-js';
import { HeadCellType, PagingRequest, Permissions, MenuChild } from '../interfaces/common';
import {
  HEAD_CELL_LABELS_MAPPING,
  HEAD_CELL_SORT_DISABLED,
  HEAD_CELL_WIDTH_MAPPING,
  Menus,
} from './appConstant';

type headCellModule =
  | keyof typeof HEAD_CELL_LABELS_MAPPING
  | keyof typeof HEAD_CELL_WIDTH_MAPPING
  | keyof typeof HEAD_CELL_SORT_DISABLED;

const CRYPTO_SECRET_KEY = process.env.REACT_APP_CRYPTO_SECRET_KEY || '';

export const checkPermissions = (
  permissions: Permissions | null,
  accessibleTo: Permissions[]
): boolean => {
  if (!permissions) return false;

  return accessibleTo.some((condition) =>
    Object.entries(condition).every(([module, actions]) => {
      const permission = permissions[module as keyof Permissions] || [];
      return actions.some((action) => permission.includes(action));
    })
  );
};

export const hasResourseAccess = (
  isSystemAdmin: string | boolean,
  permissions: Permissions | null,
  item: { accessibleTo?: Permissions[]; childrens?: MenuChild[] } | null
): boolean => {
  if (isSystemAdmin === 'true' || isSystemAdmin === true) {
    return true;
  }

  if (!item) {
    return true;
  }

  if (item.accessibleTo?.length) {
    if (checkPermissions(permissions, item.accessibleTo)) return true;
  }

  if (item.childrens?.length) {
    return item.childrens.some((child) => hasResourseAccess('false', permissions, child));
  }

  return false;
};

export const buildHeadCells = (headCellIds: string[], module: headCellModule) => {
  return headCellIds.map((headCellId) => {
    return {
      id: headCellId,
      sortEnabled: !HEAD_CELL_SORT_DISABLED[module].find((item) => item === headCellId),
      label: HEAD_CELL_LABELS_MAPPING[module][headCellId],
      width: HEAD_CELL_WIDTH_MAPPING[module][headCellId],
    } as HeadCellType;
  });
};

export const formatNumber = (num: number | string): string => {
  return typeof num === 'number' ? num.toLocaleString() : num;
};

export const formatPhone = (countryCode: string, phoneNumber: string): string => {
  const normalizedCode =
    countryCode && countryCode.startsWith('+') ? countryCode : `+${countryCode}`;

  const fullNumber = `${normalizedCode}${phoneNumber}`;

  return formatPhoneNumberIntl(fullNumber);
};

export const buildPagingResponse = (payload: PagingRequest) => {
  return {
    page: payload.page,
    per_page: payload.perPage,
    order_by: payload.orderBy,
    order: payload.order,
  };
};

export const redirectBasedOnPermission = (
  isSystemAdmin: string | boolean,
  permissions: Permissions | null,
  parentMenuId?: string
) => {
  let redirectPath = '';

  const parentMenu = Menus.find((item) => item.id === parentMenuId);
  const tempMenus = parentMenuId && parentMenu ? [parentMenu] : Menus;

  tempMenus.forEach((menu) => {
    if (menu.childrens && menu.childrens.length > 0) {
      menu.childrens.forEach((children) => {
        if (hasResourseAccess(isSystemAdmin, permissions, children)) {
          redirectPath = redirectPath || (children.redirectsTo ? children.redirectsTo : '');
        }
      });
    } else {
      if (hasResourseAccess(isSystemAdmin, permissions, menu)) {
        redirectPath = redirectPath || (menu.redirectsTo ? menu.redirectsTo : '');
      }
    }
  });

  return redirectPath;
};

export const encryptData = (data: any): string => {
  try {
    const payload = { value: data };
    const stringData = JSON.stringify(payload);
    return crypto.AES.encrypt(stringData, CRYPTO_SECRET_KEY).toString();
  } catch (error) {
    console.error('Encryption error:', error);
    return '';
  }
};

export const decryptData = (cipherText: string): any => {
  try {
    const bytes = crypto.AES.decrypt(cipherText, CRYPTO_SECRET_KEY);
    const decryptedString = bytes.toString(crypto.enc.Utf8);

    if (!decryptedString) return null;

    const parsed = JSON.parse(decryptedString);
    return parsed.value;
  } catch (error) {
    console.error('Decryption error:', error);
    return null;
  }
};

export const extractDateFromDateTimeBySpace = (dateTime: string) => {
  return dateTime ? dateTime.split(' ')[0] : '';
};

export const formatDate = (d: Date) => {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const formatDateTimeLocal = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

export const formatDateLocal = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');

  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const getStartEndOfMonth = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return { startDate: formatDate(start), endDate: formatDate(end) };
};

export const calculateNights = (checkIn: string, checkOut: string): number => {
  const inDateObj = new Date(checkIn);
  const outDateObj = new Date(checkOut);

  inDateObj.setHours(0, 0, 0, 0);
  outDateObj.setHours(0, 0, 0, 0);

  const msDiff = outDateObj.getTime() - inDateObj.getTime();

  const nightCount = msDiff / (1000 * 60 * 60 * 24);

  return nightCount || 1;
};

export const getYearLookupData = () => {
  const currentYear = new Date().getFullYear();
  const startYear = currentYear - 2;
  const endYear = currentYear + 4;
  const years = [];

  for (let year = startYear; year <= endYear; year++) {
    years.push({ id: year.toString(), name: year.toString() });
  }

  return years;
};

export const getCurrentMonthAndYear = () => {
  const now = new Date();
  const year = now.getFullYear().toString();
  const month = (now.getMonth() + 1).toString();

  return { month, year };
};

export const getYearOptions = (): { label: string; value: string }[] => {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let y = currentYear - 10; y <= currentYear + 10; y++) {
    const str = String(y);
    years.push({ label: str, value: str });
  }
  return years;
};

const MONTH_CODES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export const getCurrentMonthCode = (): string => MONTH_CODES[new Date().getMonth()];
